import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import "../../../UserDashboard/components/UserDashboardHome/UserDashboardHome.css";
import localeText from "../../../../locale";
import { Toast } from "primereact/toast";
import { Column } from "primereact/column";
import AdminService from "../../../../service/AdminService";
import { formatDate } from "../../../../utilities/formatDate";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { InputSwitch } from "primereact/inputswitch";

const PaidOrdersReport = ({ canManageInvoicing = false }) => {
  const [loading, setLoading] = useState(false);
  const [paidOrders, setPaidOrders] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [invoicingStatus, setInvoicingStatus] = useState(null);
  const [invoicingStatusLoading, setInvoicingStatusLoading] = useState(false);
  const [invoicingToggleLoading, setInvoicingToggleLoading] = useState(false);

  const adminService = new AdminService();
  const toast = useRef(null);

  async function fetchInvoicingStatus() {
    try {
      setInvoicingStatusLoading(true);
      const res = await adminService.getInvoicingStatus(
        localStorage.getItem("__extraconomy_token"),
      );
      setInvoicingStatus(res);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
        return;
      }
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("errorFetchingInvoicingStatus"),
        life: 6000,
      });
    } finally {
      setInvoicingStatusLoading(false);
    }
  }

  useEffect(() => {
    if (canManageInvoicing) fetchInvoicingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageInvoicing]);

  const handleToggleInvoicing = async (checked) => {
    if (checked && !invoicingStatus?.result?.enableAllowed) return;

    try {
      setInvoicingToggleLoading(true);
      const token = localStorage.getItem("__extraconomy_token");
      const res = checked
        ? await adminService.enableInvoicing(token)
        : await adminService.disableInvoicing(token);

      if (res?.messageCode && res.messageCode !== 200000 && res.messageCode !== 200001) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail:
            res.message ||
            localeText("data").find((msg) => msg.code === res.messageCode)?.message ||
            localeText("errorTogglingInvoicing"),
          life: 6000,
        });
      } else {
        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: checked
            ? localeText("invoicingEnabledSuccessfully")
            : localeText("invoicingDisabledSuccessfully"),
          life: 4000,
        });
      }
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
        return;
      }
      const messageCode = e.response?.data?.messageCode;
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail:
          e.response?.data?.message ||
          localeText("data").find((msg) => msg.code === messageCode)?.message ||
          localeText("errorTogglingInvoicing"),
        life: 6000,
      });
    } finally {
      setInvoicingToggleLoading(false);
      fetchInvoicingStatus();
    }
  };

  const formatDateForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [dateBegin, setDateBegin] = useState("2000-01-01");
  const [dateEnd, setDateEnd] = useState(formatDateForApi(new Date()));

  async function fetchData(dateBeginParam = dateBegin, dateEndParam = dateEnd) {
    if (dateBeginParam > dateEndParam) {
      toast.current.show({
        severity: "warn",
        summary: localeText("error"),
        detail: "A data inicial deve ser menor ou igual a data final",
        life: 5000,
      });
      return;
    }

    try {
      setLoading(true);
      const res = await adminService.getPaidOrdersReport(
        localStorage.getItem("__extraconomy_token"),
        dateBeginParam,
        dateEndParam,
      );

      setPaidOrders(res);
      setLoading(false);
    } catch (e) {
      console.log(e);
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
        return;
      }
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("errorFetchingData"),
        life: 6000,
      });
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilter = () => {
    fetchData(dateBegin, dateEnd);
  };

  const handleDownloadPdf = async () => {
    try {
      if (dateBegin > dateEnd) {
        toast.current.show({
          severity: "warn",
          summary: localeText("error"),
          detail: "A data inicial deve ser menor ou igual a data final",
          life: 5000,
        });
        return;
      }

      setDownloadingPdf(true);
      const response = await adminService.getPaidOrdersReportPdf(
        localStorage.getItem("__extraconomy_token"),
        dateBegin,
        dateEnd,
      );

      if (response && response.data) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const today = new Date();
        const dateStr = today.toISOString().split("T")[0];
        link.download = `paid-orders-report-${dateStr}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("pdfDownloadedSuccessfully"),
          life: 3000,
        });
      }
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
        return;
      }
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("errorDownloadingPdf"),
        life: 6000,
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const emptyMessage = () => {
    if (loading) return null;

    return (
      <div className="p-text-center">
        <i className="pi pi-inbox" style={{ fontSize: "3rem" }}></i>
        <p className="text-400 mt-3">{localeText("noData")}</p>
      </div>
    );
  };

  const getFirstAvailable = (row, keys = []) => {
    for (let i = 0; i < keys.length; i += 1) {
      const value = row?.[keys[i]];
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
    return null;
  };

  const formatAmount = (value) => {
    if (value === undefined || value === null || value === "") return "-";

    const localeMap = {
      pt: "pt-PT",
      it: "it-IT",
      es: "es-ES",
      en: "en-IE",
    };

    const currentLocale = localStorage.getItem("__extraconomy_locale") || "it";
    const formatter = new Intl.NumberFormat(localeMap[currentLocale] || "it-IT", {
      style: "currency",
      currency: "EUR",
    });

    if (typeof value === "number") return formatter.format(value);

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) return formatter.format(numericValue);

    return String(value);
  };

  const numberBody = (row) => {
    return getFirstAvailable(row, ["orderId"]) || "-";
  };

  const buyerBody = (row) => {
    const buyer = getFirstAvailable(row, ["buyer"]);

    const partitaivaBuyer = getFirstAvailable(row, ["partitaivaBuyer"]);

    const addressBuyer = getFirstAvailable(row, ["addressBuyer"]);

    const buyerLines = [buyer || "-"];

    if (partitaivaBuyer) {
      buyerLines.push(`CF/P.IVA: ${partitaivaBuyer}`);
    }

    if (addressBuyer) {
      buyerLines.push(`${localeText("address")}: ${addressBuyer}`);
    }

    return (
      <div className="flex flex-column gap-1">
        {buyerLines.map((line, index) => (
          <span key={`buyer-line-${index}`}>{line}</span>
        ))}
      </div>
    );
  };

  const sellerBody = (row) => {
    const seller = getFirstAvailable(row, ["seller"]);
    const partitaivaSeller = getFirstAvailable(row, ["partitaivaSeller"]);
    const addressSeller = getFirstAvailable(row, ["addressSeller"]);

    let sellerLines = [seller];

    if (partitaivaSeller) {
      sellerLines.push(`CF/P.IVA: ${partitaivaSeller}`);
    }

    if (addressSeller) {
      sellerLines.push(`${localeText("address")}: ${addressSeller}`);
    }

    return (
      <div className="flex flex-column gap-1">
        {sellerLines.map((line, index) => (
          <span key={`seller-line-${index}`}>{line}</span>
        ))}
      </div>
    );
  };

  const dateBody = (row) => {
    const rawDate = getFirstAvailable(row, ["date"]);
    if (!rawDate) return "-";

    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) return String(rawDate);
    return formatDate(parsed);
  };

  const orderAmountBody = (row) => {
    return formatAmount(getFirstAvailable(row, ["amountOrder"]));
  };

  const paidAmountBody = (row) => {
    return formatAmount(getFirstAvailable(row, ["amountPaid"]));
  };

  const commissionBody = (row) => {
    return formatAmount(getFirstAvailable(row, ["amountToPlatform"]));
  };

  const platformFeeBuyerBody = (row) => {
    return formatAmount(getFirstAvailable(row, ["amountToPlatform"]));
  };

  const platformFeeSellerBody = (row) => {
    return formatAmount(getFirstAvailable(row, ["amountToPlatform"]));
  };

  const paidToSellerBody = (row) => {
    const amountToSeller = getFirstAvailable(row, ["amountToSeller"]);

    if (amountToSeller === null) {
      return <Tag severity="warning" value={localeText("paidOrdersStatusOnHold")} />;
    }

    return formatAmount(amountToSeller);
  };

  const cashbackBody = (row) => {
    return formatAmount(getFirstAvailable(row, ["cashback"]));
  };

  const paypalBody = (row) => {
    return formatAmount(getFirstAvailable(row, ["paidPaypal"]));
  };

  const walletBody = (row) => {
    return formatAmount(getFirstAvailable(row, ["paidWallet"]));
  };

  const extraCreditBody = (row) => {
    return formatAmount(getFirstAvailable(row, ["paidExtracredit"]));
  };

  const bonificoBody = (row) => {
    return formatAmount(getFirstAvailable(row, ["paidTransferBank"]));
  };

  const statusBody = (row) => {
    return getFirstAvailable(row, ["status"]) || "-";
  };

  const invoiceStatusBody = (invoiceStatus, invoiceNumber, invoiceYear, invoiceErrorMessage) => {
    if (!invoiceStatus) return <span>—</span>;

    if (invoiceStatus === "SENT") {
      return <Tag severity="success" value={`WEB-${invoiceNumber}/${invoiceYear}`} />;
    }

    if (invoiceStatus === "PENDING") {
      return <Tag severity="warning" value={localeText("invoiceStatusPending")} />;
    }

    if (invoiceStatus === "ERROR") {
      return (
        <Tag
          severity="danger"
          value={localeText("invoiceStatusError")}
          className="invoice-error-tag"
          data-pr-tooltip={invoiceErrorMessage}
          data-pr-position="top"
        />
      );
    }

    return <span>—</span>;
  };

  const invoiceStatusBuyerBody = (row) => {
    return invoiceStatusBody(
      row.invoiceStatusBuyer,
      row.invoiceNumberBuyer,
      row.invoiceYearBuyer,
      row.invoiceErrorMessageBuyer,
    );
  };

  const invoiceStatusSellerBody = (row) => {
    return invoiceStatusBody(
      row.invoiceStatusSeller,
      row.invoiceNumberSeller,
      row.invoiceYearSeller,
      row.invoiceErrorMessageSeller,
    );
  };

  const isToggleBlocked =
    !invoicingStatus?.result?.enabled && !invoicingStatus?.result?.enableAllowed;

  return (
    <div className="card border-1 border-300">
      <Toast ref={toast} />
      <Tooltip target=".invoice-error-tag" />

      <div className="flex justify-content-between align-items-center my-4">
        <h3>{localeText("paidOrdersReport")}</h3>
        <Button
          label={localeText("exportData")}
          icon="pi pi-download"
          onClick={handleDownloadPdf}
          loading={downloadingPdf}
          disabled={downloadingPdf || loading}
          className="p-button-sm"
        />
      </div>
      <hr />

      {canManageInvoicing && (
        <>
          <div className="flex align-items-center justify-content-between gap-3 my-4 p-3 border-1 border-300 border-round">
            <div>
              <h4 className="mt-0 mb-1">{localeText("automaticInvoicing")}</h4>
              <p className="text-600 mt-0 mb-0">{localeText("automaticInvoicingDescription")}</p>
              {invoicingStatus?.result?.blockedReason && (
                <p className="text-orange-600 mt-2 mb-0 flex align-items-start gap-2">
                  <i className="pi pi-exclamation-triangle mt-1"></i>
                  <span>{invoicingStatus.result.blockedReason}</span>
                </p>
              )}
            </div>

            <Tooltip target=".invoicing-toggle-wrapper" />
            <span
              className="invoicing-toggle-wrapper"
              data-pr-tooltip={
                invoicingStatus?.result?.enableAllowed
                  ? undefined
                  : invoicingStatus?.result?.blockedReason
              }
              data-pr-position="top"
              style={{
                display: "inline-block",
                cursor: invoicingStatus?.result?.enableAllowed ? "default" : "not-allowed",
              }}
            >
              <InputSwitch
                checked={invoicingStatus?.result?.enabled}
                onChange={(e) => handleToggleInvoicing(e.value)}
                disabled={invoicingStatusLoading || invoicingToggleLoading || isToggleBlocked}
                style={{ pointerEvents: isToggleBlocked ? "none" : "auto" }}
              />
            </span>
          </div>
        </>
      )}

      <div className="flex flex-wrap align-items-end gap-3 my-4">
        <div className="flex flex-column gap-2">
          <label htmlFor="dateBegin">{localeText("startDate")}</label>
          <input
            id="dateBegin"
            type="date"
            value={dateBegin}
            onChange={(event) => setDateBegin(event.target.value)}
            className="p-inputtext p-component"
          />
        </div>

        <div className="flex flex-column gap-2">
          <label htmlFor="dateEnd">{localeText("endDate")}</label>
          <input
            id="dateEnd"
            type="date"
            value={dateEnd}
            onChange={(event) => setDateEnd(event.target.value)}
            className="p-inputtext p-component"
          />
        </div>

        <Button
          label={localeText("filter")}
          icon="pi pi-filter"
          onClick={handleApplyFilter}
          disabled={loading}
          className="p-button-sm"
        />
      </div>

      <DataTable
        className="mt-4 mb-6 p-datatable-customers"
        value={paidOrders}
        paginator
        responsiveLayout="scroll"
        loading={loading}
        dataKey="orderId"
        emptyMessage={emptyMessage()}
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate={localeText("showingFirstToLast")}
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
      >
        <Column
          field="orderId"
          sortField="orderId"
          header={localeText("paidOrdersHeaderNumber")}
          body={numberBody}
          sortable
          style={{ minWidth: "7rem" }}
        />
        <Column
          field="buyer"
          sortField="buyer"
          header={localeText("paidOrdersHeaderBuyer")}
          body={buyerBody}
          sortable
          style={{ minWidth: "16rem" }}
        />
        <Column
          field="seller"
          sortField="seller"
          header={localeText("paidOrdersHeaderSeller")}
          body={sellerBody}
          sortable
          style={{ minWidth: "16rem" }}
        />
        <Column
          field="date"
          sortField="date"
          header={localeText("paidOrdersHeaderDate")}
          body={dateBody}
          sortable
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="amountOrder"
          sortField="amountOrder"
          header={localeText("paidOrdersHeaderOrderAmount")}
          body={orderAmountBody}
          sortable
          style={{ minWidth: "9rem" }}
        />
        <Column
          field="amountPaid"
          sortField="amountPaid"
          header={localeText("paidOrdersHeaderPaidAmount")}
          body={paidAmountBody}
          sortable
          style={{ minWidth: "9rem" }}
        />
        <Column
          field="amountToPlatform"
          sortField="amountToPlatform"
          header={localeText("paidOrdersHeaderCommission")}
          body={commissionBody}
          sortable
          style={{ minWidth: "13rem" }}
        />
        <Column
          field="amountToPlatform"
          header={localeText("paidOrdersHeaderPlatformFeeBuyer")}
          body={platformFeeBuyerBody}
          style={{ minWidth: "13rem" }}
        />
        <Column
          field="amountToPlatform"
          header={localeText("paidOrdersHeaderPlatformFeeSeller")}
          body={platformFeeSellerBody}
          style={{ minWidth: "13rem" }}
        />
        <Column
          field="amountToSeller"
          sortField="amountToSeller"
          header={localeText("paidOrdersHeaderPaidToSeller")}
          body={paidToSellerBody}
          sortable
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="cashback"
          sortField="cashback"
          header="Cashback"
          body={cashbackBody}
          sortable
          style={{ minWidth: "13rem" }}
        />
        <Column
          field="paidPaypal"
          sortField="paidPaypal"
          header={localeText("paidOrdersHeaderPayPal")}
          body={paypalBody}
          sortable
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="paidWallet"
          sortField="paidWallet"
          header={localeText("paidOrdersHeaderWallet")}
          body={walletBody}
          sortable
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="paidExtracredit"
          sortField="paidExtracredit"
          header={localeText("paidOrdersHeaderExtraCredit")}
          body={extraCreditBody}
          sortable
          style={{ minWidth: "9rem" }}
        />
        <Column
          field="paidTransferBank"
          sortField="paidTransferBank"
          header={localeText("paidOrdersHeaderBankTransfer")}
          body={bonificoBody}
          sortable
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="status"
          sortField="status"
          header={localeText("paidOrdersHeaderStatus")}
          body={statusBody}
          sortable
          style={{ minWidth: "9rem" }}
        />
        <Column
          field="invoiceStatusBuyer"
          sortField="invoiceStatusBuyer"
          header={localeText("paidOrdersHeaderInvoiceBuyer")}
          body={invoiceStatusBuyerBody}
          sortable
          style={{ minWidth: "9rem" }}
        />
        <Column
          field="invoiceStatusSeller"
          sortField="invoiceStatusSeller"
          header={localeText("paidOrdersHeaderInvoiceSeller")}
          body={invoiceStatusSellerBody}
          sortable
          style={{ minWidth: "9rem" }}
        />
      </DataTable>
    </div>
  );
};

export default PaidOrdersReport;
